import { Currency } from "@/components/Currency";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { type Order } from "@repo/api";
import { getLanguageLocal, t, useTranslation } from "@repo/translation";
import {
  InlineLabelField,
  LabelField,
  Line,
  SectionContainer,
  SimpleLabelField,
  TwoColumnSectionContainer,
  localizeDateAndTime,
  translateOrderMethod,
  translateOrderPaymentStatus,
  translateOrderStatus,
  translateOrderType,
  useSettings,
} from "@repo/ui";
import { hasItems } from "@repo/utils";
import { Fragment } from "react";

interface Props {
  order: Order | undefined;
}
export const OrderDetails = ({ order }: Props) => {
  const { market, language, general } = useSettings();
  const { dateFnsLocale } = useTranslation();

  if (!order) {
    return null;
  }

  const preferredContactType =
    order?.deliveryContact?.customerContactDetails.preferredContactType;
  const paymentTransactions =
    order?.paymentPlans?.[0]?.payments?.[0]?.paymentTransactions?.[0];

  return (
    <SectionContainer>
      <TwoColumnSectionContainer>
        <LabelField
          id="order-ID"
          label={
            order.orderType === "SALJAPRO_QUOTATION"
              ? t("label.quotation_id")
              : t("label.order_id")
          }
          value={order.orderNo}
        />
        <LabelField
          id="order-status"
          label={t("label.status")}
          value={translateOrderStatus(order.orderStatus)}
        />
        <LabelField
          id="order-type"
          label={t("label.type")}
          value={translateOrderType(order.orderType)}
        />
        <LabelField
          id="order-value"
          label={t("label.value")}
          value={currencyFormatter({
            value: order.orderValue,
            currency: order.currencyCode,
            locale: getLanguageLocal(language, market),
            showSign: true,
            disableDecimals: Boolean(general?.localization?.disable_decimals),
            fractions: 2,
          })}
        />
        {order?.orderCreationDate && (
          <LabelField
            id="order-created-date"
            label={t("label.created")}
            value={localizeDateAndTime(order.orderCreationDate, dateFnsLocale)}
          />
        )}
        {order?.paymentPlans?.[0]?.dueDateTime && (
          <LabelField
            id="order-expected-payment-date"
            label={t("label.expected_payment_date")}
            value={localizeDateAndTime(
              order?.paymentPlans?.[0]?.dueDateTime,
              dateFnsLocale
            )}
          />
        )}
        <LabelField
          id="order-payment-status"
          label={t("label.payment_status")}
          value={translateOrderPaymentStatus(order.paymentPlans?.[0]?.status)}
        />
        {paymentTransactions?.transactionDateTime && (
          <LabelField
            id="order-payment-date"
            label={t("label.payment_date")}
            value={localizeDateAndTime(
              paymentTransactions?.transactionDateTime,
              dateFnsLocale
            )}
          />
        )}
        {hasItems(order?.deliveryArrangements) && (
          <LabelField
            id="order-service-type"
            label={t("label.service_type")}
            value={order?.deliveryArrangements
              .map((orderServiceType) => `${orderServiceType.serviceType}`)
              .join(", ")}
          ></LabelField>
        )}
        {/* rename to order method */}
        {order?.creationMethod && (
          <LabelField
            id="order-order-method"
            label={t("label.order_method")}
            value={translateOrderMethod(order.creationMethod)}
          />
        )}
        {order?.buCode && (
          <LabelField
            id="order-store-number"
            label={t("label.store_number")}
            value={order.buCode}
          />
        )}
        {order?.buName && (
          <LabelField
            id="order-store-name"
            label={t("label.store_name")}
            value={order.buName}
          />
        )}
        {order?.quotationExpiryDate && (
          <LabelField
            id="order-store-name"
            label={t("label.expiry_date")}
            value={localizeDateAndTime(
              order?.quotationExpiryDate,
              dateFnsLocale
            )}
          />
        )}

        {preferredContactType && (
          <LabelField
            id="order-preferred-contact-method"
            label={t("label.preferred_contact_method")}
            value={
              preferredContactType === "EMAIL"
                ? t("label.email")
                : t("label.phone")
            }
          />
        )}
      </TwoColumnSectionContainer>
      <Line />
      <div>
        <InlineLabelField
          bold
          label={t("label.goods_value")}
          value={
            <Currency
              value={order.amounts?.GoodsValue?.grossAmount ?? 0}
              currency={order.currencyCode}
            />
          }
        />

        <InlineLabelField
          bold
          label={t("label.service_value")}
          value={
            <Currency
              value={order.amounts?.Service?.grossAmount ?? 0}
              currency={order.currencyCode}
            />
          }
        />
        <InlineLabelField
          bold
          label={t("label.delivery_value")}
          value={
            <Currency
              value={order.amounts?.DeliveryService?.grossAmount ?? 0}
              currency={order.currencyCode}
            />
          }
        />
      </div>
      <Line />
      <div>
        <InlineLabelField
          bold
          label={t("label.total")}
          value={
            <Currency
              value={order.amounts?.Total?.grossAmount ?? 0}
              currency={order.currencyCode}
            />
          }
        />

        <InlineLabelField
          bold
          label={t("label.discount_value")}
          value={
            <Currency
              value={order.amounts?.Discount?.grossAmount ?? 0}
              currency={order.currencyCode}
            />
          }
        />
      </div>
      <InlineLabelField
        bold
        label={t("label.to_pay")}
        value={
          <Currency
            value={order.amounts?.ToPay?.grossAmount ?? 0}
            currency={order.currencyCode}
          />
        }
      />
    </SectionContainer>
  );
};

export const AssociatedDrawings = ({ order }: Props) => {
  if (!order?.planners) {
    return null;
  }
  const planner = order?.planners;
  return (
    <>
      <SectionContainer>
        {planner.map(({ plannerId, plannerIdSource }) => (
          <Fragment key={plannerId}>
            <SimpleLabelField label={t("label.drawing_id")} value={plannerId} />
            <SimpleLabelField
              label={t("label.drawing_type")}
              value={plannerIdSource}
            />
            <Line />
          </Fragment>
        ))}
      </SectionContainer>
    </>
  );
};
